import React, { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import { Home } from "src/model_types/home.types";
import { Box, Typography, CardMedia } from "@mui/material";
import { useNavigate } from "react-router-dom";
import L from "leaflet";

// Fix for default marker icon in react-leaflet
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

L.Marker.prototype.options.icon = DefaultIcon;

const PRIVACY_MAX_ZOOM = 14;

interface HomesMapProps {
  homes: Home[];
  height?: string;
  highlightedHomeId?: string | null;
  onHomeHoverChange?: (homeId: string | null) => void;
}

const hasValidLocation = (
  home: Home,
): home is Home & { location: { lat: number; lng: number } } => {
  if (!home.location) {
    return false;
  }

  return (
    Number.isFinite(home.location.lat) && Number.isFinite(home.location.lng)
  );
};

function AutoFitHomesBounds({
  homesWithLocation,
}: {
  homesWithLocation: Array<Home & { location: { lat: number; lng: number } }>;
}) {
  const map = useMap();

  const locationsKey = useMemo(
    () =>
      homesWithLocation
        .map((home) => `${home.location.lat}:${home.location.lng}`)
        .join("|"),
    [homesWithLocation],
  );

  useEffect(() => {
    if (homesWithLocation.length === 0) {
      return;
    }

    if (homesWithLocation.length === 1) {
      const onlyHome = homesWithLocation[0];
      map.setView([onlyHome.location.lat, onlyHome.location.lng], PRIVACY_MAX_ZOOM, {
        animate: true,
      });
      return;
    }

    const bounds = L.latLngBounds(
      homesWithLocation.map((home) => [home.location.lat, home.location.lng]),
    );

    map.fitBounds(bounds, {
      padding: [40, 40],
      maxZoom: PRIVACY_MAX_ZOOM,
      animate: true,
    });
  }, [map, locationsKey, homesWithLocation]);

  return null;
}

export default function HomesMap({
  homes,
  height = "500px",
  highlightedHomeId,
  onHomeHoverChange,
}: HomesMapProps) {
  const navigate = useNavigate();

  // Memoize to avoid refit/zoom side effects during unrelated hover rerenders
  const homesWithLocation = useMemo(() => homes.filter(hasValidLocation), [homes]);

  // Calculate map center (average of all coordinates or default to Europe)
  const getMapCenter = (): [number, number] => {
    if (homesWithLocation.length === 0) {
      return [50.0, 10.0]; // Center of Europe as default
    }
    const avgLat =
      homesWithLocation.reduce((sum, home) => sum + home.location.lat, 0) /
      homesWithLocation.length;
    const avgLng =
      homesWithLocation.reduce((sum, home) => sum + home.location.lng, 0) /
      homesWithLocation.length;
    return [avgLat, avgLng];
  };

  const handleMarkerClick = (homeId: string) => {
    navigate(`/homes/${homeId}`);
  };

  if (homesWithLocation.length === 0) {
    return (
      <Box
        sx={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f5f5f5",
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" color="text.secondary">
          No homes with location data available
        </Typography>
      </Box>
    );
  }

  return (
    <MapContainer
      center={getMapCenter()}
      zoom={5}
      maxZoom={PRIVACY_MAX_ZOOM}
      style={{ height, width: "100%", borderRadius: "8px" }}
    >
      <AutoFitHomesBounds homesWithLocation={homesWithLocation} />
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      {homesWithLocation.map((home) => {
        const homeId = home._id?.toString();
        const isHighlighted = homeId === highlightedHomeId;

        return (
          <React.Fragment key={homeId || `${home.location.lat}-${home.location.lng}`}>
            {isHighlighted && (
              <Marker
                position={[home.location.lat, home.location.lng]}
                icon={L.divIcon({
                  className: "homes-map-highlight-ring",
                  html: '<span style="display:block;width:30px;height:30px;border-radius:50%;background:rgba(57,128,209,0.18);border:2px solid rgba(57,128,209,0.7);"></span>',
                  iconSize: [30, 30],
                  iconAnchor: [15, 15],
                })}
              />
            )}

            <Marker
              position={[home.location.lat, home.location.lng]}
              eventHandlers={
                homeId
                  ? {
                      click: () => handleMarkerClick(homeId),
                      mouseover: () => onHomeHoverChange?.(homeId),
                      mouseout: () => onHomeHoverChange?.(null),
                    }
                  : undefined
              }
            >
              <Popup>
                <Box sx={{ width: 200 }}>
                  {home.pictures && home.pictures.length > 0 && (
                    <CardMedia
                      component="img"
                      height="120"
                      image={home.pictures[0]}
                      alt={`${home.city} home`}
                      sx={{ borderRadius: 1, mb: 1 }}
                    />
                  )}
                  <Typography variant="h6" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                    {home.city}, {home.country}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {home.houseType === "apartment" ? "Apartment" : "House"}
                  </Typography>
                  {home.extras?.bedrooms && (
                    <Typography variant="body2" color="text.secondary">
                      {home.extras.bedrooms} bedroom{home.extras.bedrooms > 1 ? "s" : ""}
                    </Typography>
                  )}
                </Box>
              </Popup>
            </Marker>
          </React.Fragment>
        );
      })}
    </MapContainer>
  );
}
